import {useLoadingStore} from "@/stores/loading";
import {useDrmInformationSearch} from "@/stores/drmInformationSearch";
import {useEffect, useRef, useState} from "react";
import {useSearchParams} from "react-router-dom";
import {ConditionList, DrmInformation} from "@/types/drmInformation";
import * as Common from "@/utils/common";
import {axios} from '@/lib/axios'
import {getDrmInformationList, getSearchConditionList} from '@/api/drmInformation'

export default function useDrmInformationSearchHooks() {
  const setLoading = useLoadingStore(state => state.setLoading)
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    order,
    setOrder,
    pageSize,
    setPageSize,
    checkTypeCodes,
    setCheckTypeCodes,
    searchText,
    setSearchText,
    searchYear,
    setSearchYear,
    currentPage,
    setCurrentPage
  } = useDrmInformationSearch()

  // URLクエリパラメータからストアを初期化
  const initializedRef = useRef(false);
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    const paramText = searchParams.get("keyword");
    const paramYear = searchParams.get("year");
    const paramTypes = searchParams.get("types");
    const paramOrder = searchParams.get("order");
    const paramPageSize = searchParams.get("pageSize");
    const paramPage = searchParams.get("page");

    if (paramText !== null) setSearchText(paramText);
    if (paramYear !== null) setSearchYear(paramYear);
    if (paramTypes !== null && paramTypes !== "") setCheckTypeCodes(paramTypes.split(","));
    if (paramOrder !== null) setOrder(paramOrder);
    if (paramPageSize !== null) setPageSize(paramPageSize);
    if (paramPage !== null) setCurrentPage(Number(paramPage));
  }, []);

  // 初期URLパラメータを取得（データフェッチ時のフィルタリングに使用）
  const initialParamsRef = useRef({
    keyword: searchParams.get("keyword") ?? "",
    year: searchParams.get("year") ?? "",
    types: searchParams.get("types") ? searchParams.get("types")!.split(",") : [] as string[],
    order: searchParams.get("order") ?? "1",
    pageSize: searchParams.get("pageSize") ?? "20",
    page: searchParams.get("page") ? Number(searchParams.get("page")) : 0,
  });


  const [lastModified, setLastModified] = useState("")
  const [drmInformationList, setDrmInformationList] = useState<DrmInformation[]>([])
  const [conditionList, setConditionList] = useState<ConditionList>()

  const [displayDrmInformationList, setDisplayDrmInformationList] = useState<DrmInformation[]>([])
  const [currentDrmInformationList, setCurrentDrmInformationList] = useState<DrmInformation[]>([])
  const [firstItemNum, setFirstItemNum] = useState<number>(0)
  const [lastItemNum, setLastItemNum] = useState<number>(0)

  const onClickSearchScrollRef = useRef<HTMLDivElement>(null);

  // URLクエリパラメータを更新するヘルパー
  const updateSearchParams = (params: {
    keyword?: string;
    year?: string;
    types?: string[];
    order?: string;
    pageSize?: string;
    page?: number;
  }) => {
    const newParams = new URLSearchParams();
    const keyword = params.keyword ?? searchText;
    const year = params.year ?? searchYear;
    const types = params.types ?? checkTypeCodes;
    const ord = params.order ?? order;
    const ps = params.pageSize ?? pageSize;
    const pg = params.page ?? 0;

    if (keyword) newParams.set("keyword", keyword);
    if (year) newParams.set("year", year);
    if (types.length > 0) newParams.set("types", types.join(","));
    if (ord && ord !== "1") newParams.set("order", ord);
    if (ps && ps !== "20") newParams.set("pageSize", ps);
    if (pg > 0) newParams.set("page", String(pg));

    setSearchParams(newParams, { replace: true });
  };

  const onClickSearch = () => {
    const filterDrmInformations = Common.filterDrmInformations(drmInformationList, checkTypeCodes, searchText || "", searchYear);
    setDisplayDrmInformationList(filterDrmInformations);
    const {
      firstItemNum,
      lastItemNum,
      currentItems
    } = Common.paginateCurrentValues(0, pageSize, filterDrmInformations);
    setFirstItemNum(firstItemNum);
    setLastItemNum(lastItemNum);
    setCurrentDrmInformationList(currentItems);
    setCurrentPage(0);
    updateSearchParams({ page: 0 });
    Common.smoothScroll(onClickSearchScrollRef, -70);
  }

  //検索オプション＿情報区分
  const onClickCheckType = (code: string) => {
    const newIds = checkTypeCodes.includes(code) ? checkTypeCodes.filter(typeCode => typeCode !== code) : [...checkTypeCodes, code]
    setCheckTypeCodes(newIds);
  }

  //ページネーション＿表示件数変更
  const onChangePageSize = (e: any) => {
    setPageSize(e.target.value)
    const {
      firstItemNum,
      lastItemNum,
      currentItems
    } = Common.paginateCurrentValues(0, e.target.value, displayDrmInformationList)
    setFirstItemNum(firstItemNum)
    setLastItemNum(lastItemNum)
    setCurrentDrmInformationList(currentItems)
    setCurrentPage(0)
    updateSearchParams({ pageSize: e.target.value, page: 0 });
  }
  //ページネーション＿ページ移動
  const onChangePage = (e: any) => {
    setCurrentPage(e.selected)
    const {
      firstItemNum,
      lastItemNum,
      currentItems
    } = Common.paginateCurrentValues(e.selected, pageSize, displayDrmInformationList)
    setFirstItemNum(firstItemNum)
    setLastItemNum(lastItemNum)
    setCurrentDrmInformationList(currentItems)
    updateSearchParams({ page: e.selected });
  }

  // ページネーション＿先頭ページへ
  const goToFirstPage = () => {
    setCurrentPage(0);
    const {
      firstItemNum,
      lastItemNum,
      currentItems
    } = Common.paginateCurrentValues(0, pageSize, displayDrmInformationList);
    setFirstItemNum(firstItemNum);
    setLastItemNum(lastItemNum);
    setCurrentDrmInformationList(currentItems);
    updateSearchParams({ page: 0 });
  };

// ページネーション＿最後ページへ
  const goToLastPage = () => {
    const lastPage = Math.ceil(displayDrmInformationList.length / Number(pageSize)) - 1;
    setCurrentPage(lastPage);
    const {
      firstItemNum,
      lastItemNum,
      currentItems
    } = Common.paginateCurrentValues(lastPage, pageSize, displayDrmInformationList);
    setFirstItemNum(firstItemNum);
    setLastItemNum(lastItemNum);
    setCurrentDrmInformationList(currentItems);
    updateSearchParams({ page: lastPage });
  };

  // 検索オプション・キーワード取得
  useEffect(() => {
    getSearchConditionList().then(result => {
      setConditionList(result.data)
    })
  }, [])

  // お知らせ一覧の取得
  useEffect(() => {
    setLoading(true)
    const initParams = initialParamsRef.current;
    const effectiveOrder = initParams.order || order;
    getDrmInformationList(effectiveOrder).then(result => {
      const drmInformationData = Common.addDisplayNum(result.data.drm_information_list)
      setDrmInformationList(drmInformationData)
      setLastModified(result.data.last_modified)

      // 初回ロード時はURLパラメータの値を優先的に使う
      const effectiveTypeCodes = initParams.types.length > 0 ? initParams.types : checkTypeCodes;
      const effectiveSearchText = initParams.keyword || searchText;
      const effectiveSearchYear = initParams.year || searchYear;
      const effectivePageSize = initParams.pageSize || pageSize;
      const effectiveCurrentPage = initParams.page || currentPage;

      const filterDrmInformations = Common.filterDrmInformations(drmInformationData, effectiveTypeCodes, effectiveSearchText, effectiveSearchYear)
      setDisplayDrmInformationList(filterDrmInformations)
      //ページネーション
      const {
        firstItemNum,
        lastItemNum,
        currentItems
      } = Common.paginateCurrentValues(effectiveCurrentPage, effectivePageSize, filterDrmInformations)
      setFirstItemNum(firstItemNum)
      setLastItemNum(lastItemNum)
      setCurrentDrmInformationList(currentItems)

      // 初回パラメータをクリア（以降は通常のストア値を使う）
      initialParamsRef.current = { keyword: "", year: "", types: [], order: "", pageSize: "", page: 0 };
    }).catch(error => {
      console.log(error)
    }).finally(() => {
      setLoading(false)
    })
  }, [order])

  // 並び順変更（URL同期付き）
  const handleSetOrder = (newOrder: string) => {
    setOrder(newOrder);
    updateSearchParams({ order: newOrder, page: 0 });
    setCurrentPage(0);
  };

  return {
    lastModified,
    onClickSearch,
    conditionList,
    onClickCheckType,
    checkTypeCodes,
    displayDrmInformationList,
    firstItemNum,
    lastItemNum,
    pageSize,
    onChangePageSize,
    order,
    setOrder: handleSetOrder,
    goToFirstPage,
    onChangePage,
    currentPage,
    goToLastPage,
    currentDrmInformationList,
    onClickSearchScrollRef
  };
}